import { useEffect, useState } from "react";
import { darkenColor, getTextColor } from "@repo/utils";

import type { ButtonProps } from "./button";
import { useHover } from "~/hooks/use-hover";
import { cn } from "~/lib/utils";
import { useTheme } from "~/providers/theme-provider";
import { Button } from "./button";

interface RegisterButtonProps extends ButtonProps {
  borderClassName?: string;
  isPressed?: boolean;
  textColor?: string;
}

export function RegisterButton({
  children,
  disabled,
  className,
  color,
  textColor,
  borderClassName,
  isPressed,
  ...props
}: RegisterButtonProps) {
  const { theme } = useTheme();

  if (!color) {
    if (props.variant) {
      switch (props.variant) {
        case "success":
          color = "#008000";
          break;
        case "secondary":
          color = "#4a6283";
          break;
        case "warning":
          color = "#FFA500";
          break;
        case "destructive":
          color = "#D2042D";
          break;
        case "outline":
          
          break;
        case "primary":
          color = "#5A57E2";
          break;
        case "default":
        default:
          color = theme === "light" ? "#aabbcc" : "#464d54";
          break;
      }
    } else {
      color = theme === "light" ? "#aabbcc" : "#464d54";
    }
  }

  const [pressed, setPressed] = useState(isPressed);

  const { hoverProps, style } = useHover(
    {
      backgroundImage: `linear-gradient(190deg, ${darkenColor(-0.6, color)}, ${darkenColor(-0.2, color)})`,
      color: getTextColor(color),
    },
    {
      backgroundImage: `linear-gradient(190deg, ${darkenColor(-0.4, color)}, ${color})`,
      color: getTextColor(color),
    },
  );

  useEffect(() => {
    setPressed(isPressed);
  }, [isPressed]);

  return (
    <Button
      {...props}
      {...hoverProps}
      style={{
        ...style,
        ...props.style,
      }}
      onPointerDown={(e) => {
        setPressed(true);
        props.onPointerDown?.(e);
      }}
      onPointerLeave={(e) => {
        props.onPointerLeave?.(e);
        hoverProps.onPointerLeave();
        if (!isPressed) {
          setPressed(false);
        }
      }}
      onPointerUp={(e) => {
        props.onPointerUp?.(e);
        if (!isPressed) {
          setPressed(false);
        }
      }}
      className={cn(
        "relative group h-auto w-auto overflow-hidden text-sm font-medium disabled:cursor-not-allowed",
        pressed && "active:shadow-inner active:shadow-black",
        className,
        borderClassName,
      )}
      disabled={disabled}
    >
      <div
        className={cn(
          "absolute inset-0 shadow-inner shadow-white",
          pressed && "shadow-none",
        )}
      />
      <div
        className={cn(
          "flex min-h-fit w-full flex-col items-center justify-center px-2 py-2",
          className,
        )}
      >
        {children}
      </div>
    </Button>
  );
}
